import type { SalesBreakdownRow, SalesDimension } from '@horevia/contracts';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ChartFigure,
  Container,
  EmptyState,
  ErrorState,
  HorizontalBars,
  NoPermissionState,
  PageHeader,
  SegmentedSwitch,
  Skeleton,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { BarChart3, Download } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MetricCard } from '@/features/analytics/MetricCard';
import { useCan, useHasModule, useSession } from '@/features/auth/session';
import { InsightsToolbar } from '@/features/insights/InsightsToolbar';
import {
  SALES_AXES,
  SALES_AXIS_GROUP_BY,
  breakdownLabel,
  isItemAxis,
  percentLabel,
} from '@/features/insights/labels';
import { salesAnalyticsQueryOptions } from '@/features/insights/queries';
import { validateInsightsSearch, type InsightsSearch } from '@/features/insights/search';
import { useInsightsPeriod } from '@/features/insights/useInsightsPeriod';
import { outletsQueryOptions } from '@/features/pos/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatNumber } from '@/lib/format';
import { useScopedPropertyId } from '@/lib/property';

export const Route = createFileRoute('/_app/analytics/sales')({
  validateSearch: validateInsightsSearch,
  component: SalesPage,
});

const ROWS_BY_AXIS: Record<SalesDimension, keyof SalesRows> = {
  OUTLET: 'byOutlet',
  CATEGORY: 'byCategory',
  PRODUCT: 'byProduct',
  HOUR: 'byHour',
  WEEKDAY: 'byWeekday',
  SERVER: 'byServer',
  SERVICE_TYPE: 'byServiceType',
};

interface SalesRows {
  byOutlet: SalesBreakdownRow[];
  byCategory: SalesBreakdownRow[];
  byProduct: SalesBreakdownRow[];
  byHour: SalesBreakdownRow[];
  byWeekday: SalesBreakdownRow[];
  byServer: SalesBreakdownRow[];
  byServiceType: SalesBreakdownRow[];
}

/** Ventes POS (ADR-024, D-H57) : ventilations par axe, jour de service (R-ANA-02). */
function SalesPage() {
  const { t, i18n } = useTranslation(['insights', 'analytics', 'common', 'errors']);
  const locale = i18n.language;
  const session = useSession();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const canRead = useCan('report.operational');
  const canReadFinance = useCan('report.financial');
  const hasPos = useHasModule('pos');
  const propertyId = useScopedPropertyId();
  const { preset, custom, range, compare } = useInsightsPeriod(search);
  const [axis, setAxis] = useState<SalesDimension>('OUTLET');

  const setSearch = (next: Partial<InsightsSearch>) =>
    void navigate({ search: (current) => ({ ...current, ...next }), replace: true });

  const outlets = useQuery(outletsQueryOptions(propertyId, canRead && hasPos));
  const query = useQuery(
    salesAnalyticsQueryOptions(
      { ...range, compare, propertyId, outletId: search.outletId },
      canRead && hasPos,
    ),
  );
  const data = query.data;
  const currency = data?.currencyCode ?? session.user.tenant.currencyCode;
  const money = (value: number) => formatAmount(value, locale, currency);

  if (!canRead) {
    return (
      <Container>
        <PageHeader title={t('insights:sales.title')} />
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const rows = data ? data[ROWS_BY_AXIS[axis]] : [];
  const perItem = isItemAxis(axis);
  const axisLabel = t(`insights:axes.${axis}`);

  return (
    <Container>
      <PageHeader
        title={t('insights:sales.title')}
        subtitle={t('insights:sales.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('insights:title'), link: <Link to="/analytics">{t('insights:title')}</Link> },
          { label: t('insights:sales.title') },
        ]}
        actions={
          <Button variant="secondary" asChild>
            <Link
              to="/reports/$kind"
              params={{ kind: 'sales' }}
              search={{
                period: preset,
                from: custom.from,
                to: custom.to,
                groupBy: SALES_AXIS_GROUP_BY[axis],
              }}
            >
              <Download className="ico" aria-hidden="true" />
              {t('insights:actions.openReport')}
            </Link>
          </Button>
        }
      />

      <div className="stack">
        <Card>
          <CardBody>
            <InsightsToolbar
              preset={preset}
              onPresetChange={(value) => setSearch({ period: value })}
              range={range}
              custom={custom}
              onCustomChange={(value) =>
                setSearch({ period: 'CUSTOM', from: value.from, to: value.to })
              }
              compare={compare}
              onCompareChange={(value) => setSearch({ compare: value || undefined })}
              outlets={outlets.data ?? []}
              outletId={search.outletId}
              onOutletChange={(value) => setSearch({ outletId: value })}
            />
          </CardBody>
        </Card>

        {!hasPos ? (
          <EmptyState
            icon={<BarChart3 className="ico ico-24" aria-hidden="true" />}
            title={t('insights:sales.title')}
            description={t('insights:noModule.pos')}
          />
        ) : query.isError ? (
          <ErrorState
            title={t('common:states.errorTitle')}
            description={errorMessage(query.error, t)}
            traceId={errorTraceId(query.error)}
            retryLabel={t('common:actions.retry')}
            onRetry={() => void query.refetch()}
            traceCopyLabel={t('common:actions.copy')}
            traceCopiedLabel={t('common:actions.copied')}
          />
        ) : (
          <>
            <div className="grid grid-4">
              <MetricCard
                label={t('insights:kpi.salesRevenue')}
                display={money(data?.totals.revenue.value ?? 0)}
                metric={data?.totals.revenue}
                loading={query.isPending}
                showDelta={compare}
              />
              <MetricCard
                label={t('insights:kpi.orders')}
                display={formatNumber(data?.totals.ordersCount.value ?? 0, locale)}
                metric={data?.totals.ordersCount}
                loading={query.isPending}
                showDelta={compare}
                footer={t('insights:kpi.ordersFoot')}
              />
              <MetricCard
                label={t('insights:kpi.covers')}
                display={formatNumber(data?.totals.coversCount.value ?? 0, locale)}
                metric={data?.totals.coversCount}
                loading={query.isPending}
                showDelta={compare}
              />
              <MetricCard
                label={t('insights:kpi.averageTicket')}
                display={money(data?.totals.averageTicket.value ?? 0)}
                metric={data?.totals.averageTicket}
                loading={query.isPending}
                showDelta={compare}
              />
              <MetricCard
                label={t('insights:kpi.happyHour')}
                display={money(data?.totals.happyHourTotal.value ?? 0)}
                metric={data?.totals.happyHourTotal}
                loading={query.isPending}
                higherIsBetter={false}
                showDelta={compare}
              />
              <MetricCard
                label={t('insights:kpi.transferred')}
                display={money(data?.totals.transferredTotal.value ?? 0)}
                metric={data?.totals.transferredTotal}
                loading={query.isPending}
                showDelta={compare}
                footer={t('insights:kpi.transferredFoot', {
                  count: data?.totals.transferredCount.value ?? 0,
                })}
              />
              <MetricCard
                label={t('insights:kpi.cancelled')}
                display={formatNumber(data?.totals.cancelledCount.value ?? 0, locale)}
                metric={data?.totals.cancelledCount}
                loading={query.isPending}
                higherIsBetter={false}
                showDelta={compare}
              />
            </div>

            <Card>
              <CardHeader
                title={t('insights:filters.axis')}
                icon={<BarChart3 className="ico ico-16" aria-hidden="true" />}
              />
              <CardBody>
                <SegmentedSwitch
                  label={t('insights:filters.axis')}
                  value={axis}
                  onValueChange={setAxis}
                  wrap
                  options={SALES_AXES.map((value) => ({
                    value,
                    label: t(`insights:axes.${value}`),
                  }))}
                />
              </CardBody>
            </Card>

            {query.isPending ? (
              <Skeleton height={260} />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<BarChart3 className="ico ico-24" aria-hidden="true" />}
                title={t('insights:empty.title')}
                description={t('insights:empty.body')}
              />
            ) : (
              <div className="grid grid--two-thirds">
                <Card>
                  <CardHeader title={axisLabel} />
                  <CardBody density="flush">
                    <TableWrap>
                      <Table compact>
                        <caption className="sr-only">
                          {t('insights:table.caption', { axis: axisLabel, count: rows.length })}
                        </caption>
                        <thead>
                          <tr>
                            <Th>{t('insights:table.label')}</Th>
                            <Th align="right">{t('insights:table.orders')}</Th>
                            {perItem ? (
                              <Th align="right">{t('insights:table.quantity')}</Th>
                            ) : (
                              <Th align="right">{t('insights:table.covers')}</Th>
                            )}
                            <Th align="right">{t('insights:table.revenue')}</Th>
                            <Th align="right">{t('insights:table.share')}</Th>
                            {perItem ? null : (
                              <Th align="right">{t('insights:table.averageTicket')}</Th>
                            )}
                            <Th align="right">{t('insights:table.happyHour')}</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row) => (
                            <Tr key={row.key || row.label}>
                              <Td>{breakdownLabel(t, axis, row)}</Td>
                              <Td numeric align="right">
                                {formatNumber(row.ordersCount, locale)}
                              </Td>
                              <Td numeric align="right">
                                {perItem
                                  ? formatNumber(row.quantity, locale)
                                  : formatNumber(row.coversCount ?? 0, locale)}
                              </Td>
                              <Td numeric align="right">
                                {money(row.revenue)}
                              </Td>
                              <Td numeric align="right">
                                {percentLabel(row.share, locale)}
                              </Td>
                              {perItem ? null : (
                                <Td numeric align="right">
                                  {money(row.averageTicket ?? 0)}
                                </Td>
                              )}
                              <Td numeric align="right">
                                {money(row.happyHourTotal)}
                              </Td>
                            </Tr>
                          ))}
                        </tbody>
                      </Table>
                    </TableWrap>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader title={t('insights:table.revenue')} />
                  <CardBody>
                    <ChartFigure
                      description={t('insights:charts.breakdownDescription', {
                        axis: t(`insights:axisSingular.${axis}`),
                        count: rows.length,
                      })}
                    >
                      <HorizontalBars
                        items={rows.slice(0, 12).map((row) => ({
                          key: row.key || row.label,
                          label: breakdownLabel(t, axis, row),
                          value: row.revenue,
                          display: money(row.revenue),
                          sub: percentLabel(row.share, locale),
                        }))}
                      />
                    </ChartFigure>
                  </CardBody>
                </Card>
              </div>
            )}

            {data && canReadFinance && data.byPaymentMethod && data.byPaymentMethod.length > 0 ? (
              <Card>
                <CardHeader title={t('insights:sections.paymentsByMethod')} />
                <CardBody>
                  <HorizontalBars
                    items={data.byPaymentMethod.map((row) => ({
                      key: `${row.method}|${row.provider ?? ''}`,
                      label: [
                        t(`insights:methods.${row.method}`, { defaultValue: row.method }),
                        row.provider,
                      ]
                        .filter(Boolean)
                        .join(' · '),
                      value: row.amount,
                      display: money(row.amount),
                      sub: `${t('insights:charts.columns.count')} : ${formatNumber(row.count, locale)}`,
                    }))}
                  />
                </CardBody>
              </Card>
            ) : null}
          </>
        )}
      </div>
    </Container>
  );
}
